
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import GameScreen from './redesign/GameScreen';
import { useGameUI } from './hooks/useGameUI';
import { GameState, ArtistPersonality, NeedsState } from '@/types/career-sim';
import { toast } from 'sonner';

const CareerSimGame = () => {
  const [gameInitialized, setGameInitialized] = useState(false);
  const [selectedPersonalityId, setSelectedPersonalityId] = useState<string | null>(null);
  
  // Initialize with default game state
  const [gameState, setGameState] = useState<GameState>({
    initialized: false,
    artistPersonality: null,
    career: {
      artistLevel: 1,
      careerStage: 'bedroom',
      daysActive: 0,
      energy: 100,
      mentalHealth: 100,
      fanbase: 0,
      funds: 500,
      reputation: 0,
      creativeEnergy: 100,
      totalReleases: 0,
      skills: {
        production: 10,
        performance: 10,
        marketing: 10,
        business: 10,
        networking: 10
      },
      releases: [],
      gigs: [],
      achievements: []
    },
    environment: {
      currentLocation: 'bedroom-studio',
      studioQuality: 1,
      availableVenues: ['bedroom-studio', 'local-venue'],
      time: {
        hour: 9,
        day: 1,
        week: 1
      },
      weather: 'sunny'
    },
    inventory: {
      equipment: [],
      clothes: [],
      items: []
    },
    needs: {
      energy: 100,
      mentalHealth: 100,
      physicalHealth: 90,
      creativity: 80,
      social: 70,
      finances: 60
    },
    actions: [],
    events: [],
    challenges: [],
    currentMood: 'energetic',
    activeAction: null,
    activeEvent: null,
    multiplayerChallenges: [],
    notifications: [],
    lastSaved: null,
    currentWeek: 1,
    weeklyGoals: null,
    careerPhase: 'bedroom',
    actionCount: 0,
    unlockedActions: ['create-music', 'rest', 'social-media'],
    seenEvents: []
  });
  
  // UI helper hooks
  const gameUI = useGameUI(gameState, updateGameState);
  
  // Available artist personalities
  const artistPersonalities: ArtistPersonality[] = [
    {
      id: 'hustler',
      type: 'hustler',
      name: 'The Hustler',
      description: 'Business-minded artist focused on commercial success and strategic growth.',
      strengths: ['Marketing', 'Networking', 'Business'],
      weaknesses: ['Creativity', 'Authenticity'],
      idealPath: 'Record Label Executive',
      avatar: '/avatars/hustler.png',
      startingStats: {
        energy: 90,
        mentalHealth: 85,
        fanbase: 10,
        funds: 800,
        reputation: 10,
        creativeEnergy: 75
      },
      traits: {
        productivity: 85,
        creativity: 60,
        performance: 65,
        business: 90,
        marketing: 85,
        resilience: 70
      },
      animationStyle: {
        idle: 'determined',
        working: 'focused',
        performing: 'engaging',
        networking: 'charismatic',
        exhausted: 'frustrated'
      }
    },
    {
      id: 'innovator',
      type: 'innovator',
      name: 'The Innovator',
      description: 'Experimental artist pushing boundaries and creating new sounds.',
      strengths: ['Creativity', 'Authenticity', 'Production'],
      weaknesses: ['Marketing', 'Business'],
      idealPath: 'Acclaimed Producer',
      avatar: '/avatars/innovator.png',
      startingStats: {
        energy: 85,
        mentalHealth: 80,
        fanbase: 5,
        funds: 500,
        reputation: 5,
        creativeEnergy: 95
      },
      traits: {
        productivity: 70,
        creativity: 95,
        performance: 60,
        business: 40,
        marketing: 50,
        resilience: 65
      },
      animationStyle: {
        idle: 'thoughtful',
        working: 'inspired',
        performing: 'expressive',
        networking: 'awkward',
        exhausted: 'drained'
      }
    },
    {
      id: 'purist',
      type: 'purist',
      name: 'The Purist',
      description: 'Authentic artist dedicated to artistic integrity above commercial concerns.',
      strengths: ['Authenticity', 'Performance', 'Creativity'],
      weaknesses: ['Business', 'Networking'],
      idealPath: 'Respected Artist',
      avatar: '/avatars/purist.png',
      startingStats: {
        energy: 80,
        mentalHealth: 90,
        fanbase: 10,
        funds: 400,
        reputation: 15,
        creativeEnergy: 90
      },
      traits: {
        productivity: 75,
        creativity: 90,
        performance: 85,
        business: 30,
        marketing: 40,
        resilience: 85
      },
      animationStyle: {
        idle: 'composed',
        working: 'focused',
        performing: 'passionate',
        networking: 'genuine',
        exhausted: 'contemplative'
      }
    }
  ];
  
  // Character selection handler
  const handleSelectPersonality = (personalityId: string) => {
    const selectedPersonality = artistPersonalities.find(p => p.id === personalityId);
    
    if (selectedPersonality) {
      setSelectedPersonalityId(personalityId);
      
      // Initialize game state with selected personality
      updateGameState({
        initialized: true,
        artistPersonality: selectedPersonality,
        career: {
          ...gameState.career,
          energy: selectedPersonality.startingStats.energy,
          mentalHealth: selectedPersonality.startingStats.mentalHealth,
          fanbase: selectedPersonality.startingStats.fanbase,
          funds: selectedPersonality.startingStats.funds,
          reputation: selectedPersonality.startingStats.reputation,
          creativeEnergy: selectedPersonality.startingStats.creativeEnergy
        },
        needs: {
          energy: selectedPersonality.startingStats.energy,
          mentalHealth: selectedPersonality.startingStats.mentalHealth,
          physicalHealth: 85,
          creativity: selectedPersonality.startingStats.creativeEnergy * 0.8,
          social: 70,
          finances: selectedPersonality.startingStats.funds > 600 ? 70 : 50
        }
      });
      
      // Save selection to localStorage
      localStorage.setItem('selectedPersonality', personalityId);
      setGameInitialized(true);
      
      toast.success(`Selected ${selectedPersonality.name}`, {
        description: "Your artist journey begins!",
      });
    }
  };
  
  // Load saved game state from localStorage on initial render
  useEffect(() => {
    const saved = localStorage.getItem('careerSimGameState');
    const savedPersonalityId = localStorage.getItem('selectedPersonality');
    
    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        setGameState(parsedState);
        setGameInitialized(true);
        
        if (parsedState.artistPersonality) {
          setSelectedPersonalityId(parsedState.artistPersonality.id);
        }
      } catch (error) {
        console.error("Error loading saved game state:", error);
        // If error loading saved state, check if we have a personality selection
        if (savedPersonalityId) {
          handleSelectPersonality(savedPersonalityId);
        }
      }
    } else if (savedPersonalityId) {
      handleSelectPersonality(savedPersonalityId);
    }
  }, []);
  
  // Save game state to localStorage when it changes
  useEffect(() => {
    if (gameInitialized) {
      localStorage.setItem('careerSimGameState', JSON.stringify({
        ...gameState,
        lastSaved: new Date()
      }));
    }
  }, [gameState, gameInitialized]);
  
  // Game state update function
  function updateGameState(updates: Partial<GameState>) {
    setGameState(prevState => ({
      ...prevState,
      ...updates
    }));
  }
  
  // Set up simulated needs decay over time
  useEffect(() => {
    if (!gameInitialized) return;
    
    const needsTimer = setInterval(() => {
      setGameState(prevState => {
        // Calculate decays based on personality and circumstances
        const energyDecay = 0.2;
        const mentalHealthDecay = 0.15;
        const creativityDecay = 0.25;
        const socialDecay = 0.1;
        
        // Apply decay and ensure values don't go below 0
        const updatedNeeds: NeedsState = {
          energy: Math.max(0, prevState.needs.energy - energyDecay),
          mentalHealth: Math.max(0, prevState.needs.mentalHealth - mentalHealthDecay),
          physicalHealth: Math.max(0, prevState.needs.physicalHealth - 0.1),
          creativity: Math.max(0, prevState.needs.creativity - creativityDecay),
          social: Math.max(0, prevState.needs.social - socialDecay),
          finances: prevState.needs.finances // Finances don't decay naturally
        };
        
        // Determine mood based on needs
        let newMood: GameState['currentMood'] = prevState.currentMood;
        
        if (updatedNeeds.energy < 20) {
          newMood = 'exhausted';
        } else if (updatedNeeds.mentalHealth < 25) {
          newMood = 'stressed';
        } else if (updatedNeeds.creativity > 85) {
          newMood = 'creative';
        } else if (updatedNeeds.energy > 75 && updatedNeeds.mentalHealth > 80) {
          newMood = 'energetic';
        } else if (updatedNeeds.mentalHealth < 40) {
          newMood = 'frustrated';
        } else if (updatedNeeds.creativity < 30) {
          newMood = 'burnt-out';
        } else {
          newMood = 'focused';
        }
        
        return {
          ...prevState,
          needs: updatedNeeds,
          currentMood: newMood
        };
      });
    }, 5000); // Update needs every 5 seconds
    
    return () => clearInterval(needsTimer);
  }, [gameInitialized]);
  
  // Render character selection if no personality is selected
  if (!gameState.initialized || !gameState.artistPersonality) {
    return (
      <CharacterSelection 
        personalities={artistPersonalities}
        onSelect={handleSelectPersonality}
        selectedId={selectedPersonalityId}
      />
    );
  }
  
  return (
    <GameScreen 
      gameState={gameState} 
      updateGameState={updateGameState} 
    />
  );
};

// Component definitions for the existing ActionPanel, CareerStatsPanel, and CharacterSelection
const ActionPanel = ({ gameState, updateGameState, onStartAction }) => {
  const actions = [
    { id: 'create-music', name: 'Create Music', energyCost: 10, icon: '🎵', description: 'Create a new track or song' },
    { id: 'rest', name: 'Rest & Recover', energyCost: -20, icon: '💤', description: 'Recharge your energy and mental health' },
    { id: 'network', name: 'Network', energyCost: 15, icon: '🤝', description: 'Connect with other music professionals' }
  ];
  
  return (
    <div className="bg-black/20 rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3">Available Actions</h3>
      <div className="space-y-2">
        {actions.map(action => (
          <button
            key={action.id}
            className="w-full bg-black/30 hover:bg-black/40 text-left p-3 rounded-md flex items-center transition-colors"
            onClick={() => onStartAction(action)}
            disabled={gameState.activeAction !== null}
          >
            <span className="text-2xl mr-3">{action.icon}</span>
            <div>
              <div className="font-medium">{action.name}</div>
              <div className="text-xs text-gray-300">{action.description}</div>
            </div>
            <div className="ml-auto px-2 py-1 rounded bg-black/30 text-xs">
              {action.energyCost > 0 ? `-${action.energyCost} energy` : `+${Math.abs(action.energyCost)} energy`}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const CareerStatsPanel = ({ gameState, updateGameState }) => {
  return (
    <div className="bg-black/20 rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3">Career Status</h3>
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span>Level:</span>
          <span>{gameState.career.artistLevel}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Fans:</span>
          <span>{gameState.career.fanbase}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Funds:</span>
          <span>${gameState.career.funds}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Reputation:</span>
          <span>{gameState.career.reputation}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Career Stage:</span>
          <span className="capitalize">{gameState.career.careerStage}</span>
        </div>
      </div>
    </div>
  );
};

const CharacterSelection = ({ personalities, onSelect, selectedId }) => {
  return (
    <div className="w-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 text-center"
      >
        <h2 className="text-2xl font-bold mb-2">Choose Your Artist Personality</h2>
        <p className="text-muted-foreground">Each personality has unique strengths and career trajectories</p>
      </motion.div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {personalities.map((personality) => (
          <motion.div
            key={personality.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 * personalities.indexOf(personality) }}
          >
            <button
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                selectedId === personality.id 
                  ? 'border-purple-500 bg-purple-500/10' 
                  : 'border-gray-700 bg-black/20 hover:bg-black/30'
              }`}
              onClick={() => onSelect(personality.id)}
            >
              <h3 className="text-lg font-semibold mb-2">{personality.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{personality.description}</p>
              
              <div className="mb-3">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Strengths</div>
                <div className="flex flex-wrap gap-1">
                  {personality.strengths.map(strength => (
                    <span 
                      key={strength} 
                      className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-300"
                    >
                      {strength}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="mb-3">
                <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Weaknesses</div>
                <div className="flex flex-wrap gap-1">
                  {personality.weaknesses.map(weakness => (
                    <span 
                      key={weakness} 
                      className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-300"
                    >
                      {weakness}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="text-xs text-gray-400">Career Path: {personality.idealPath}</div>
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default CareerSimGame;
